/**
 * unreadDivider — where the "new messages" rule goes, as a leaf.
 *
 * The transcript already tells you that new messages exist (the bottom pill
 * counts arrivals while you are scrolled up) but never where they START. On
 * a thread you left yesterday and come back to, the pill says "12 new" and
 * you still have to guess which twelve.
 *
 * The boundary is the earliest message the viewer has not seen. Two rules
 * make the answer stable enough to render:
 *
 *   - The walk runs BACKWARD from the tail and stops at the first row that
 *     was already read. Everything before a read row is read, so there is no
 *     reason to keep looking, and it means one old undatable row deep in the
 *     transcript cannot drag the divider to the top.
 *   - Rows that are not the viewer's reading — their own messages, system
 *     notices, context-only rows — are TRANSPARENT: skipped without counting
 *     and without stopping the walk. A teammate's message, your reply, then
 *     another teammate's message is one unread run, not two.
 *
 * A thread that has never been viewed returns null rather than marking its
 * whole history unread: "everything here is new" is true and useless, and it
 * would put a rule above the first message of every thread you open.
 *
 * Leaf module: no React, no DOM, so the arithmetic is pinnable without
 * rendering the chat surface.
 */
import type { ChatMessage } from './types';
import { messageOrderTs } from '../../services/groupThreads/seenBy';

export interface UnreadBoundaryInput {
  /** `lastViewedAt` as epoch ms. Non-finite (never viewed) yields null. */
  lastViewedAtMs: number;
  /** The viewer, so their own messages stay transparent. */
  selfUid: string | null;
}

/**
 * Is this row part of what the viewer reads, or chrome they scroll past?
 *
 * "Own" is resolved through the SAME ladder the scroll controller's arrival
 * classifier uses (`senderId` first, with the assistant sentinel, then the
 * `isUser` role split for solo threads and every pre-group-threading
 * message). A third definition of "mine" is how the pill, the thread rail
 * and this divider would start disagreeing about the same message.
 */
function isReadableByViewer(m: ChatMessage, selfUid: string | null): boolean {
  if (m.isSystem) return false;
  if (m.isContextOnly) return false;
  if (m.senderId) {
    if (m.senderId === 'assistant') return true;
    return !(selfUid && m.senderId === selfUid);
  }
  return !m.isUser;
}

/**
 * Index of the earliest message the viewer has not seen, or null when there
 * is no honest boundary to draw.
 */
export function firstUnreadIndex(
  messages: ReadonlyArray<ChatMessage>,
  input: UnreadBoundaryInput,
): number | null {
  const { lastViewedAtMs, selfUid } = input;
  if (!Number.isFinite(lastViewedAtMs)) return null;
  let boundary: number | null = null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (!m) continue;
    if (!isReadableByViewer(m, selfUid)) continue;
    const ts = messageOrderTs(m);
    // An undatable row cannot be judged either way. Skipping keeps it
    // transparent rather than letting it end the run at an arbitrary point.
    if (ts === null) continue;
    if (ts > lastViewedAtMs) {
      boundary = i;
      continue;
    }
    break;
  }
  return boundary;
}

/** `lastViewedAt` (an ISO string on the thread record) as epoch ms. */
export function lastViewedAtMs(lastViewedAt: string | null | undefined): number {
  if (typeof lastViewedAt !== 'string' || !lastViewedAt) return NaN;
  const ms = new Date(lastViewedAt).getTime();
  return Number.isFinite(ms) ? ms : NaN;
}
